/**
 * Penalize Inspector Modal
 *
 * Modal for applying penalty points to inspector
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Slider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import BaseActionModal, { IActionModalProps, IModalChildProps } from './BaseActionModal';
import { ArbitrationAction } from '../../../services/arbitrationService';

interface PenalizeInspectorModalProps extends Omit<IActionModalProps, 'action'> {}

const PenalizeInspectorContent: React.FC<IModalChildProps> = ({
  setPayload,
  setIsValid
}) => {
  const [reason, setReason] = useState('');
  const [inspectorPenaltyPoints, setInspectorPenaltyPoints] = useState(10);
  const [restrictUser, setRestrictUser] = useState(false);

  useEffect(() => {
    setPayload({
      reason,
      inspectorPenaltyPoints,
      restrictUser
    });
    setIsValid(reason.length >= 5 && inspectorPenaltyPoints > 0);
  }, [reason, inspectorPenaltyPoints, restrictUser, setPayload, setIsValid]);

  return (
    <Box>
      <Alert severity="error" icon={<ReportProblemIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          با اعمال جریمه، هم امتیاز منفی به بازرس تعلق می‌گیرد و هم همین درصد از «هزینه بازرسی»
          به‌صورت مالی از موجودی قابل‌برداشت بازرس کسر می‌شود (مثلاً ۱۰ یعنی ۱۰٪ هزینه بازرسی).
        </Typography>
      </Alert>

      {/* Penalty Points / % of inspection fee */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          میزان جریمه (٪ از هزینه بازرسی + امتیاز منفی):
        </Typography>
        <Slider
          value={inspectorPenaltyPoints}
          onChange={(_, value) => setInspectorPenaltyPoints(value as number)}
          min={1}
          max={50}
          step={1}
          marks={[
            { value: 1, label: '۱' },
            { value: 10, label: '۱۰' },
            { value: 25, label: '۲۵' },
            { value: 50, label: '۵۰' }
          ]}
          valueLabelDisplay="on"
          color="error"
        />
      </Box>

      {/* Reason */}
      <TextField
        fullWidth
        multiline
        rows={2}
        label="دلیل جریمه"
        placeholder="مثال: بازرسی غیردقیق و نادرست انجام شده است"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        sx={{ mb: 3 }}
        helperText="حداقل ۵ کاراکتر"
      />

      {/* Restrict Inspector */}
      <Box sx={{
        p: 2,
        bgcolor: 'warning.50',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'warning.200'
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={restrictUser}
              onChange={(e) => setRestrictUser(e.target.checked)}
              color="warning"
            />
          }
          label="محدود کردن بازرس از پذیرش بازرسی‌های جدید"
        />
      </Box>
    </Box>
  );
};

const PenalizeInspectorModal: React.FC<PenalizeInspectorModalProps> = (props) => {
  return (
    <BaseActionModal {...props} action={ArbitrationAction.PENALIZE_INSPECTOR}>
      <PenalizeInspectorContent
        payload={{}}
        setPayload={() => {}}
        isValid={false}
        setIsValid={() => {}}
      />
    </BaseActionModal>
  );
};

export default PenalizeInspectorModal;
